/**
 * Orchestrates `analyze_image_for_model_selection` in the browser: metadata
 * hints → deterministic slice sampling → optional image classification →
 * reconciliation → Contour+ model filtering. React-free; the image, renderer,
 * classifier and model list are injected so this is unit-testable.
 *
 * Only enums and model names leave this function. Pixel data goes solely to
 * the injected `classify` (the `/classify-slices` route); DICOM free text and
 * coordinates never leave the browser.
 */
import {
    SLICE_CLASSIFICATION_MODALITIES,
    type SliceClassificationModality,
} from '@agent/tool-names';
import type { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import type {
    AnalyzeImageForModelSelectionOutput,
    SliceRegionLabel,
} from '../../contract';
import {
    aggregateCoverage,
    bodyPartMatchesCoverage,
    metadataEvidence,
    parseRegionsFromText,
    reconcile,
    regionsForBodyPart,
    type MetadataHints,
    type SliceLabel,
} from './bodyRegion';
import { clinicalFocusFor } from './clinicalFocus';
import { pickRepresentativeSlices } from './sampleSlices';
import type { ModelSelectionTrace } from './debugTrace';

export interface ImageForAnalysis<S> {
    readonly modality: string;
    readonly hints: MetadataHints;
    /** Slices already ordered along the slice normal. */
    readonly orderedSlices: ReadonlyArray<S>;
    /** Slice normal within ~25° of the patient S–I axis (see isAxialStack). */
    readonly isAxial: boolean;
    isUsable(slice: S): boolean;
}

export interface AnalyzeImageDeps<S> {
    image(): Promise<ImageForAnalysis<S> | undefined>;
    contourModels(): ReadonlyArray<PredictionModel> | undefined;
    renderPng(slice: S): Promise<string>;
    /** Throws when classification is unavailable (route off, network). */
    classify(
        pngBase64: string[],
        modality: SliceClassificationModality
    ): Promise<ReadonlyArray<SliceRegionLabel>>;
    /** Developer-only: receives the full decision trace, including the raw
     *  DICOM hint text. Never forwarded to the server or the chat model. */
    onTrace?(trace: ModelSelectionTrace): void;
    /** Offline evaluation only: which stack positions (%) to classify. The
     *  viewer never sets it and always uses SAMPLE_PERCENTILES. */
    readonly samplePercentiles?: ReadonlyArray<number>;
}

function unavailable(reason: string): AnalyzeImageForModelSelectionOutput {
    return { status: 'unavailable', reason };
}

/** Non-default customization names, as the Contour+ form submits them. */
function customizationNames(model: PredictionModel): string[] {
    return (model.customizations ?? [])
        .filter((c) => c.name !== model.name)
        .map((c) => c.name.split('.').pop() ?? '')
        .filter(Boolean);
}

export type VisionResult =
    | {
          readonly labels: SliceLabel[];
          /** The exact PNGs sent to the classifier; debug trace only. */
          readonly pngBase64: string[];
      }
    | { readonly unavailableReason: string };

async function classifySamples<S>(
    image: ImageForAnalysis<S>,
    deps: AnalyzeImageDeps<S>
): Promise<VisionResult> {
    const modality = SLICE_CLASSIFICATION_MODALITIES.find(
        (m) => m === image.modality
    );
    if (modality === undefined) {
        return {
            unavailableReason: `image classification supports CT and MR only (this is ${image.modality})`,
        };
    }
    // CT is acquired axially; MR often is not, and a sagittal or coronal
    // slice shows several regions at once.
    if (modality === 'MR' && !image.isAxial) {
        return {
            unavailableReason:
                'image classification supports axial MR only (this series is sagittal, coronal or oblique)',
        };
    }
    const samples = pickRepresentativeSlices(
        image.orderedSlices,
        image.isUsable,
        deps.samplePercentiles
    );
    if (samples.length === 0) {
        return { unavailableReason: 'no usable slices to classify' };
    }
    try {
        const pngs = await Promise.all(
            samples.map((s) => deps.renderPng(s.slice))
        );
        const regions = await deps.classify(pngs, modality);
        if (regions.length !== samples.length) {
            return {
                unavailableReason:
                    'the classifier returned a wrong label count',
            };
        }
        return {
            pngBase64: pngs,
            labels: samples.map((s, i) => ({
                percentile: s.percentile,
                sliceIndex: s.sliceIndex,
                region: regions[i],
            })),
        };
    } catch (err) {
        // Vision is optional evidence: fall back to metadata only. The
        // message is built by our own route client (status code only).
        return {
            unavailableReason:
                err instanceof Error
                    ? err.message
                    : 'image classification failed',
        };
    }
}

export async function analyzeImageForModelSelection<S>(
    deps: AnalyzeImageDeps<S>
): Promise<AnalyzeImageForModelSelectionOutput> {
    const image = await deps.image();
    if (!image || image.orderedSlices.length === 0) {
        return unavailable('no image is loaded in the viewer');
    }
    const models = deps.contourModels();
    if (!models) {
        return unavailable(
            'the Contour+ model list is not loaded yet; open the viewer ' +
                'workflows once and try again'
        );
    }

    const metadata = metadataEvidence(image.hints);
    const vision = await classifySamples(image, deps);
    const visionEvidence = 'labels' in vision ? vision.labels : undefined;
    const result = reconcile(
        metadata,
        visionEvidence ? aggregateCoverage(visionEvidence) : []
    );

    // Same modality rule as the manual Contour+ form's default filter.
    const modalityFits = (m: PredictionModel): boolean =>
        m.inputModalities.length === 0 ||
        m.inputModalities.includes(image.modality);
    const compatibleModels = models
        .filter(modalityFits)
        .filter((m) =>
            bodyPartMatchesCoverage(m.bodyPart, result.detectedBodyRegions)
        )
        .map((m) => ({
            modelName: m.name,
            label: m.label,
            bodyPart: m.bodyPart,
            clinicalFocus: clinicalFocusFor(m.name),
            customizationNames: customizationNames(m),
        }));

    deps.onTrace?.({
        modality: image.modality,
        sliceCount: image.orderedSlices.length,
        hints: [
            ['BodyPartExamined', image.hints.bodyPartExamined],
            ['SeriesDescription', image.hints.seriesDescription],
            ['ProtocolName', image.hints.protocolName],
        ].map(([tag, text]) => ({
            tag,
            text,
            parsedRegions: parseRegionsFromText(text),
        })),
        metadata,
        vision,
        visionCoverage: visionEvidence ? aggregateCoverage(visionEvidence) : [],
        result,
        models: models.map((m) => ({
            name: m.name,
            bodyPart: m.bodyPart,
            bodyPartRegions: regionsForBodyPart(m.bodyPart),
            modalities: m.inputModalities,
            modalityFits: modalityFits(m),
            included: compatibleModels.some((c) => c.modelName === m.name),
        })),
    });

    return {
        status: 'ok',
        detectedBodyRegions: result.detectedBodyRegions,
        confidence: result.confidence,
        metadataEvidence: metadata,
        visionEvidence: visionEvidence ?? [],
        visionAvailable: visionEvidence !== undefined,
        ...('unavailableReason' in vision
            ? { visionUnavailableReason: vision.unavailableReason }
            : {}),
        metadataConflict: result.metadataConflict,
        compatibleModels,
        method: result.method,
    };
}
